<template>
  <div class="generator-review card p-4 mb-3">
    <!-- Basic Information -->
    <h4>{{ $t("signup.se.generator_review.basic_information") }}</h4>

    <!-- Production Units -->
    <div
      v-for="(unit, index) in localGenerator.application_information
        .new_production_unit_applications"
      :key="index"
      class="border rounded p-3 mb-3"
    >
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5>
          {{ $t("signup.se.generator_review.production_unit") }} #{{
            Number(index) + 1
          }}
        </h5>
        <button
          v-if="index === 1"
          @click="removeProductionUnit(index)"
          class="btn btn-outline-danger btn-sm"
        >
          {{ $t("common.remove") }}
        </button>
      </div>

      <SelectFieldComponent
        v-model="unit.technology"
        field="signup.se.generator_review.technology"
        :options="technologyOptions"
        :rules="rules.required"
      />
      <template v-if="unit.technology === SeGeneratorType.THERMAL">
        <SelectFieldComponent
          v-model="unit.thermal_subtype"
          field="signup.se.generator_review.thermal_subtype"
          :options="thermalSubtypeOptions"
          :rules="rules.required"
        />
        <CheckboxFieldComponent
          v-model="unit.thermal_is_chp"
          field="signup.se.generator_review.thermal_is_chp"
        />
      </template>
      <TextFieldComponent
        v-model="unit.device_name"
        field="signup.se.generator_review.device_name"
        :rules="rules.required"
      />
      <NumberFieldComponent
        v-model="unit.device_power"
        field="signup.se.generator_review.device_power"
        :rules="rules.devicePower"
      />
      <TextFieldComponent
        v-if="unit.unit_type === 'new_device'"
        v-model="unit.commissioning_date"
        type="date"
        field="signup.se.generator_review.commissioning_date"
        :rules="rules.commissioningDate"
      />
      <TextFieldComponent
        v-model="unit.property_designation"
        field="signup.se.generator_review.property_designation"
        :rules="rules.required"
      />
      <template v-if="unit.unit_type === 'new_device'">
        <TextFieldComponent
          v-model="unit.postal_code"
          field="signup.se.generator_review.postal_code"
          :rules="rules.required"
        />
        <TextFieldComponent
          v-model="unit.city"
          field="signup.se.generator_review.city"
          :rules="rules.required"
        />
      </template>
    </div>

    <div v-if="canAddMoreUnits" class="mb-4">
      <button @click="addProductionUnit" class="btn btn-outline-primary">
        {{ $t("common.add_another_unit") }}
      </button>
    </div>

    <!-- Metering Information -->
    <h4 class="mt-4">
      {{ $t("signup.se.generator_review.metering_information") }}
    </h4>
    <TextFieldComponent
      v-if="!conversionFromNationalToEECS"
      v-model="goApplication.grid_area_id"
      field="signup.se.generator_review.grid_area_id"
      :rules="rules.gridAreaId"
    />
    <TextFieldComponent
      v-model="goApplication.grid_production_device_id"
      field="signup.se.generator_review.grid_production_device_id"
      :rules="rules.gridProductionDeviceId"
    />
    <template v-if="!conversionFromNationalToEECS">
      <TextFieldComponent
        v-model="goApplication.grid_eidel_id"
        field="signup.se.generator_review.grid_eidel_id"
        :rules="rules.gridEidelId"
      />
      <SelectFieldComponent
        v-model="goApplication.grid_metered_by_hour"
        field="signup.se.generator_review.grid_metered_by_hour"
        :options="YES_NO_OPTIONS"
      />
      <SelectFieldComponent
        v-model="goApplication.grid_concession"
        field="signup.se.generator_review.grid_concession"
        :options="YES_NO_OPTIONS"
      />
    </template>
    <SelectFieldComponent
      v-model="goApplication.metering_location"
      field="signup.se.generator_review.metering_location"
      :options="meteringLocationOptions"
      :rules="rules.required"
    />
    <SignupFieldComponent
      v-if="
        goApplication.metering_location !==
        MeteringLocation.DIRECTLY_BEFORE_GRID_CONNECTION
      "
      field="signup.se.generator_review.correction_factor_info"
    />
    <NumberFieldComponent
      v-model="goApplication.holding_percentage"
      field="signup.se.generator_review.holding_percentage"
      :rules="rules.holdingPercentage"
    />

    <!-- Investment Support Section -->
    <template v-if="!conversionFromNationalToEECS">
      <h4 class="mt-4">
        {{ $t("signup.se.generator_review.investment_support") }}
      </h4>
      <div class="row mb-2 g-2">
        <label class="col-sm-12 pb-0 col-form-label">
          {{ $t("signup.se.generator_review.investment_support_question") }}
        </label>
        <div class="col-sm-11">
          <div
            class="form-check"
            v-for="option in investmentSupportOptions"
            :key="option.value"
          >
            <input
              class="form-check-input"
              type="radio"
              name="investment-support"
              :id="`investment-support-${slugify(option.value)}`"
              :value="option.value"
              v-model="
                localGenerator.application_information.go_application
                  .investment_support
              "
            />
            <label
              class="form-check-label"
              :for="`investment-support-${slugify(option.value)}`"
            >
              {{ $t(option.label) }}
            </label>
          </div>
        </div>
      </div>

      <!-- Investment Support Details -->
      <template
        v-if="
          localGenerator.application_information.go_application
            .investment_support === InvestmentSupport.YES
        "
      >
        <NumberFieldComponent
          v-model="goApplication.investment_support_amount"
          field="signup.se.generator_review.investment_support_amount"
          :rules="rules.investmentSupportAmount"
        />
        <TextFieldComponent
          v-model="goApplication.investment_support_date"
          type="date"
          field="signup.se.generator_review.investment_support_date"
          :rules="rules.required"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import CheckboxFieldComponent from "@/signup/common/CheckboxFieldComponent.vue";
import { enumOptions, YES_NO_OPTIONS } from "@/signup/common/fieldOptions";
import NumberFieldComponent from "@/signup/common/NumberFieldComponent.vue";
import SelectFieldComponent from "@/signup/common/SelectFieldComponent.vue";
import SignupFieldComponent from "@/signup/common/SignupFieldComponent.vue";
import TextFieldComponent from "@/signup/common/TextFieldComponent.vue";
import type { Account, Generator } from "@/types";
import { computed, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  CorrectionFactor,
  FlowType,
  InvestmentSupport,
  MeteringLocation,
  RegistrationStatus,
  SeGeneratorType,
  SeThermalSubtype,
} from "./enums";
import type { SeGoApplication, SeProductionUnit } from "./types";

const props = withDefaults(
  defineProps<{
    generatorIndex: number;
    companyIndex: number;
    accountData: Account;
    conversionFromNationalToEECS?: boolean;
  }>(),
  { conversionFromNationalToEECS: false }
);

const emit = defineEmits<{
  handleUpdateGenerator: [generator: Generator];
}>();

const { t } = useI18n();
const {
  required,
  requiredMin,
  requiredMinMax,
  reasonableDate,
  integer,
  min,
  exactDigitLength,
  exactStringLength,
} = useValidators(t);

const rules = {
  required: { required },
  devicePower: { required, integer, min: min(0) },
  commissioningDate: { required, reasonableDate },
  gridAreaId: { exactStringLength: exactStringLength(3) },
  gridProductionDeviceId: { exactDigitLength: exactDigitLength(18) },
  gridEidelId: { exactDigitLength: exactDigitLength(5) },
  holdingPercentage: { requiredMinMax: requiredMinMax(0, 100) },
  investmentSupportAmount: { requiredMin: requiredMin(0) },
};

const technologyOptions = enumOptions(
  SeGeneratorType,
  "signup.se.generator_review.technology.options"
);
const thermalSubtypeOptions = enumOptions(
  SeThermalSubtype,
  "signup.se.generator_review.thermal_subtype.options"
);
const meteringLocationOptions = enumOptions(
  MeteringLocation,
  "signup.se.generator_review.metering_location.options"
);
const investmentSupportOptions = Object.values(InvestmentSupport).map(
  (value) => ({
    value,
    label: `signup.se.generator_review.investment_support_options.${value
      .toLowerCase()
      .replace(/ /g, "_")}`,
  })
);

const generator = computed(
  () =>
    props.accountData.companies[props.companyIndex].generators[
      props.generatorIndex
    ]
);

const createEmptyUnit = (): SeProductionUnit => {
  if (props.conversionFromNationalToEECS) {
    return {
      unit_type: "conversion",
      device_name: "",
      device_power: null,
      technology: null,
      property_designation: "",
      estimated_annual_production: null,
      thermal_subtype: null,
      thermal_is_chp: null,
    };
  }
  return {
    unit_type: "new_device",
    device_name: "",
    device_power: null,
    commissioning_date: "",
    property_designation: "",
    postal_code: "",
    city: "",
    technology: null,
    estimated_annual_production: null,
    thermal_subtype: null,
    thermal_is_chp: null,
  };
};

const cleanProductionUnit = (unit: SeProductionUnit): SeProductionUnit => {
  const baseFields = {
    device_name: unit.device_name,
    device_power: unit.device_power,
    technology: unit.technology,
    property_designation: unit.property_designation,
    estimated_annual_production: unit.estimated_annual_production,
    thermal_subtype: unit.thermal_subtype,
    thermal_is_chp: unit.thermal_is_chp,
  };

  if (props.conversionFromNationalToEECS) {
    return { unit_type: "conversion", ...baseFields };
  }

  // Not `=== "new_device"`: units saved before the discriminator existed carry no unit_type, and all of them are new devices.
  const newDevice = unit.unit_type === "conversion" ? null : unit;
  return {
    unit_type: "new_device",
    ...baseFields,
    commissioning_date: newDevice?.commissioning_date ?? "",
    postal_code: newDevice?.postal_code ?? "",
    city: newDevice?.city ?? "",
  };
};

const cleanGoApplication = (
  goApplication: SeGoApplication | null | undefined
): Partial<SeGoApplication> => {
  if (!goApplication) {
    return {};
  }

  if (props.conversionFromNationalToEECS) {
    // For conversion flow, exclude non-EECS fields
    const {
      grid_area_id: _grid_area_id,
      grid_eidel_id: _grid_eidel_id,
      grid_metered_by_hour: _grid_metered_by_hour,
      grid_concession: _grid_concession,
      investment_support: _investment_support,
      investment_support_amount: _investment_support_amount,
      investment_support_date: _investment_support_date,
      grid_owner_name: _grid_owner_name,
      grid_owner_corporate_id: _grid_owner_corporate_id,
      ...keptFields
    } = goApplication;

    return keptFields;
  } else {
    // For new device flow, ensure all fields are present (keep existing data)
    return goApplication;
  }
};

// Initialize with default values
const localGenerator = reactive({
  ...generator.value,
  application_information: {
    ...generator.value.application_information,
    new_production_unit_applications: [
      // Clean existing units or create a new one if none exist
      ...(generator.value.application_information?.new_production_unit_applications?.map(
        cleanProductionUnit
      ) || []),
      // If no units exist, create one empty unit
      ...(!generator.value.application_information
        ?.new_production_unit_applications?.length
        ? [createEmptyUnit()]
        : []),
    ],
    go_application: {
      // Defaults first (fallback values)
      metering_location: MeteringLocation.DIRECTLY_BEFORE_GRID_CONNECTION,
      holding_percentage: 100.0,
      // Only set excluded fields for non-conversion flow
      ...(!props.conversionFromNationalToEECS && {
        investment_support: InvestmentSupport.NO,
        investment_support_amount: null,
        investment_support_date: null,
        grid_metered_by_hour: true,
        grid_concession: true,
      }),
      // Spread existing user data (overrides defaults)
      ...cleanGoApplication(
        generator.value.application_information?.go_application
      ),
      // Enforce correction_factor default (after spreading to prevent null override)
      correction_factor:
        cleanGoApplication(
          generator.value.application_information?.go_application
        )?.correction_factor ?? CorrectionFactor.STANDARD_VALUE,
      // Enforce flow-specific fields last
      registration_status: props.conversionFromNationalToEECS
        ? RegistrationStatus.REGISTERED
        : RegistrationStatus.NOT_REGISTERED,
    },
    flow_type:
      generator.value.application_information?.flow_type ||
      (props.conversionFromNationalToEECS
        ? FlowType.CONVERSION
        : FlowType.NEW_DEVICE),
  },
});

const goApplication = computed(
  () => localGenerator.application_information.go_application
);

const canAddMoreUnits = computed(() => {
  return (
    localGenerator.application_information.new_production_unit_applications
      .length < 2
  );
});

const addProductionUnit = () => {
  if (canAddMoreUnits.value) {
    localGenerator.application_information.new_production_unit_applications.push(
      createEmptyUnit()
    );
  }
};

const removeProductionUnit = (index: number) => {
  if (index > 0) {
    localGenerator.application_information.new_production_unit_applications.splice(
      index,
      1
    );
  }
};

watch(
  localGenerator,
  (newValue) => {
    emit("handleUpdateGenerator", newValue);
  },
  { deep: true }
);

const slugify = (str: string) => {
  return String(str).toLowerCase().replace(/\s+/g, "-");
};
</script>

<style scoped lang="scss">
.generator-review {
  .col-form-label {
    text-align: left;
    font-weight: bold;
    padding: 0;
  }

  .form-check {
    margin-bottom: 0.5rem;
  }

  h4 {
    margin-bottom: 1rem;
  }

  .border {
    border-color: #dee2e6 !important;
  }
}

@media (max-width: 575.98px) {
  .generator-review {
    .col-form-label {
      margin-top: 1rem;
    }
  }
  h4 {
    font-size: 1.2rem;
  }
}
</style>
